import { useEffect, useRef } from "react";

const ShimmerDots = () => {
	const canvasRef = useRef(null);
	const rafRef = useRef(0);
	const dotsRef = useRef([]);
	const mouseRef = useRef({ x: -9999, y: -9999 });

	// Config - exactly as you described
	const cfgRef = useRef({
		// visual - subtle dots that blend into your beige background
		colorRGB: "139,133,123", // using your existing text color for natural blend
		dotRadius: 1.5, // slightly bigger dots
		highlightRadius: 80, // mouse interaction area
		// layout - bottom-left extending further toward top right
		maxCoverageX: 0.65, // extend further horizontally toward top right
		maxCoverageY: 0.65, // extend further vertically toward top right
		padding: 30, // keep away from edges
		spacing: 14, // tighter grid spacing
		// dynamics - "manual shimmer"
		fullAlpha: 0.8, // near full opacity on hover
		ease: 0.12, // smooth transitions
	});

	const buildDots = (canvas, ctx) => {
		const dpr = Math.min(window.devicePixelRatio || 1, 2);
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		// Match canvas size to viewport for proper coordinate mapping
		canvas.style.width = vw + "px";
		canvas.style.height = vh + "px";
		canvas.width = Math.floor(vw * dpr);
		canvas.height = Math.floor(vh * dpr);
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		ctx.clearRect(0, 0, vw, vh);

		const { spacing, padding, maxCoverageX, maxCoverageY, dotRadius } =
			cfgRef.current;

		dotsRef.current = [];

		// Rectangular grid: originates bottom-left, extends toward top-right
		const maxX = vw * maxCoverageX * 0.78;
		const maxY = vh * (1 - maxCoverageY) * 1.1;

		// Create rectangular grid from bottom-left corner
		const gridWidth = maxX - padding;
		const gridHeight = vh - padding - maxY;

		for (let y = vh - padding; y >= maxY; y -= spacing) {
			for (let x = padding; x <= maxX; x += spacing) {
				// Distance from origin (bottom-left corner) for opacity fade
				const distFromOrigin =
					Math.hypot(x - padding, vh - padding - y) /
					Math.hypot(gridWidth, gridHeight);

				// Calculate distance from edges for edge fading
				const distFromRightEdge = (maxX - x) / gridWidth;
				const distFromTopEdge = (y - maxY) / gridHeight;

				// Edge fade: reduce opacity near boundaries with random variation
				const edgeFadeThreshold = 0.55; // fade within 15% of edges
				const randomEdgeFactor = 0.5 + Math.random() * 0.5; // 0.5 to 1.0 random multiplier

				let edgeFade = 1;
				if (distFromRightEdge < edgeFadeThreshold) {
					edgeFade *= Math.min(
						edgeFade,
						(distFromRightEdge / edgeFadeThreshold) * randomEdgeFactor
					);
				}
				if (distFromTopEdge < edgeFadeThreshold) {
					edgeFade *= Math.min(
						edgeFade,
						(distFromTopEdge / edgeFadeThreshold) * randomEdgeFactor
					);
				}

				// Each dot has mostly random opacity, faded into background
				const randomBase = Math.random();
				// Lower opacity as distance increases from bottom-left origin
				const fadeByDistance = 1 - distFromOrigin * 0.5; // less aggressive distance fade
				// Apply edge fading
				const combinedFade = fadeByDistance * edgeFade;
				// Keep the footer dots subtle; interaction supplies the emphasis.
				const baseAlpha = (0.025 + randomBase * 0.075) * combinedFade;

				dotsRef.current.push({
					x: x + (Math.random() - 0.5) * 3, // tighter random offset for linear grid
					y: y + (Math.random() - 0.5) * 3,
					base: baseAlpha,
					a: baseAlpha,
					r: dotRadius + Math.random() * 0.3, // less size variation for linear look
				});
			}
		}
	};

	const draw = (ctx) => {
		const { colorRGB, fullAlpha, ease, highlightRadius } = cfgRef.current;
		const dots = dotsRef.current;
		if (!dots.length) return;

		const mx = mouseRef.current.x;
		const my = mouseRef.current.y;

		let anyChanging = false;

		// Clear the full backing canvas independently of the capped render DPR.
		ctx.save();
		ctx.setTransform(1, 0, 0, 1, 0, 0);
		ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
		ctx.restore();

		for (let i = 0; i < dots.length; i++) {
			const d = dots[i];

			// Distance-based highlighting for "manual shimmer" effect
			const dx = mx - d.x;
			const dy = my - d.y;
			const dist = Math.hypot(dx, dy);

			// Mouse hover increases to near full opacity (as you specified)
			const target =
				dist < highlightRadius
					? d.base + (fullAlpha - d.base) * (1 - dist / highlightRadius)
					: d.base;

			// Smooth easing
			const next = d.a + (target - d.a) * ease;
			if (Math.abs(next - d.a) > 0.001) anyChanging = true;
			d.a = next;

			// Draw each dot individually for proper opacity blending
			ctx.fillStyle = `rgba(${colorRGB},${d.a})`;
			ctx.beginPath();
			ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
			ctx.fill();
		}

		// Keep animating while things are changing
		if (anyChanging) {
			rafRef.current = requestAnimationFrame(() => draw(ctx));
		} else {
			rafRef.current = 0;
		}
	};

	useEffect(() => {
		// Desktop-only guard
		if (typeof window === "undefined" || window.innerWidth < 1024) return;

		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d", { alpha: true });
		buildDots(canvas, ctx);
		draw(ctx);

		const onMove = (e) => {
			mouseRef.current.x = e.clientX;
			mouseRef.current.y = e.clientY;
			if (!rafRef.current) {
				rafRef.current = requestAnimationFrame(() => draw(ctx));
			}
		};

		const onLeave = () => {
			mouseRef.current.x = -9999;
			mouseRef.current.y = -9999;
			if (!rafRef.current) {
				rafRef.current = requestAnimationFrame(() => draw(ctx));
			}
		};

		const onResize = () => {
			buildDots(canvas, ctx);
			if (!rafRef.current) {
				rafRef.current = requestAnimationFrame(() => draw(ctx));
			}
		};

		window.addEventListener("mousemove", onMove, { passive: true });
		window.addEventListener("mouseleave", onLeave, { passive: true });
		window.addEventListener("resize", onResize);

		return () => {
			window.removeEventListener("mousemove", onMove);
			window.removeEventListener("mouseleave", onLeave);
			window.removeEventListener("resize", onResize);
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		};
		// The animation owns its listeners and canvas lifecycle for one mount.
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<canvas
			ref={canvasRef}
			className="pointer-events-none absolute bottom-0 left-0 z-[1] hidden lg:block rounded-[200px]"
			aria-hidden="true"
		/>
	);
};

export default ShimmerDots;
